import React, { useState, useEffect, useCallback } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Cell } from 'recharts';
import { fmt, api } from '../utils/api';
import useCollapsible from '../utils/useCollapsible';

const pctFmt = (v) => (v == null ? '—' : `${v >= 0 ? '' : ''}${v.toFixed(1)}%`);

function PeriodCard({ label, period, targetRate }) {
  if (!period) return null;
  const isWithdrawing = period.netWithdrawal >= 0;
  const rateColor = period.withdrawalRatePct == null
    ? '#6b7280'
    : period.withdrawalRatePct > targetRate ? '#ef4444' : '#00d4aa';

  return (
    <div style={styles.periodCard}>
      <div style={styles.periodLabel}>{label}</div>
      <div style={styles.periodMainRow}>
        <span style={styles.periodDollar}>
          {isWithdrawing ? 'Withdrew ' : 'Contributed '}
          {fmt.currency(Math.abs(period.netWithdrawal))}
        </span>
      </div>
      <div style={styles.periodStatsRow}>
        <div style={styles.periodStat}>
          <span style={styles.periodStatLabel}>Withdrawal rate</span>
          <span style={{ ...styles.periodStatValue, color: rateColor }}>{pctFmt(period.withdrawalRatePct)}</span>
        </div>
        <div style={styles.periodStat}>
          <span style={styles.periodStatLabel}>Portfolio return</span>
          <span style={{ ...styles.periodStatValue, color: period.portfolioReturnPct == null ? '#6b7280' : (period.portfolioReturnPct >= 0 ? '#00d4aa' : '#ef4444') }}>
            {pctFmt(period.portfolioReturnPct)}
          </span>
        </div>
      </div>
    </div>
  );
}

function TransferReviewQueue({ candidates, onReview, reviewingId }) {
  if (!candidates || candidates.length === 0) return null;

  return (
    <div style={styles.reviewBox}>
      <div style={styles.reviewHeader}>
        🔍 {candidates.length} transfer{candidates.length === 1 ? '' : 's'} to review
      </div>
      <p style={styles.reviewSubtext}>
        These look like they moved money to/from an investment account based on the description, but haven't been confirmed
        yet - they aren't counted in the numbers above until you confirm them.
      </p>
      {candidates.map((c) => (
        <div key={c.id} style={styles.reviewRow}>
          <div style={styles.reviewRowInfo}>
            <div style={styles.reviewRowDesc}>{c.description}</div>
            <div style={styles.reviewRowMeta}>
              {new Date(`${c.date}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} &middot; {c.accountName}
            </div>
          </div>
          <div style={{
            ...styles.reviewBadge,
            color: c.direction === 'withdrawal' ? '#00d4aa' : '#f97316',
            borderColor: c.direction === 'withdrawal' ? 'rgba(0,212,170,0.35)' : 'rgba(249,115,22,0.35)',
          }}>
            {c.direction === 'withdrawal' ? 'Withdrawal' : 'Contribution'} &middot; {fmt.currency(c.displayAmount)}
          </div>
          <div style={styles.reviewActions}>
            <button
              style={styles.confirmBtn}
              disabled={reviewingId === c.id}
              onClick={() => onReview(c.id, 'confirm')}
            >
              ✓ Confirm
            </button>
            <button
              style={styles.ignoreBtn}
              disabled={reviewingId === c.id}
              onClick={() => onReview(c.id, 'ignore')}
            >
              ✕ Not this
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function InvestmentWithdrawals({ targetWithdrawalRate, liquidNetWorth }) {
  const [collapsed, toggleCollapsed] = useCollapsible('investmentWithdrawals');
  const [data, setData] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [reviewingId, setReviewingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, c] = await Promise.all([
        api.getInvestmentWithdrawals(),
        api.getTransferCandidates(),
      ]);
      setData(d);
      setCandidates(c.candidates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleReview = async (transactionId, action) => {
    setReviewingId(transactionId);
    try {
      await api.reviewTransferCandidate(transactionId, action);
      setCandidates((prev) => prev.filter((c) => c.id !== transactionId));
      if (action === 'confirm') {
        // Numbers above now depend on this confirmation - refresh them
        const d = await api.getInvestmentWithdrawals();
        setData(d);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setReviewingId(null);
    }
  };

  const targetRate = targetWithdrawalRate ?? data?.targetWithdrawalRate ?? 4;

  const t12 = data?.trailing12;
  const t12RateOverTarget = t12?.withdrawalRatePct != null && t12.withdrawalRatePct > targetRate;
  const t12BeatingReturn = t12?.withdrawalRatePct != null && t12?.portfolioReturnPct != null
    ? t12.withdrawalRatePct > t12.portfolioReturnPct
    : null;

  const chartData = (data?.history || []).map((h) => ({
    month: h.month.slice(5),
    withdrawalRate: h.withdrawalRatePct,
    portfolioReturn: h.portfolioReturnPct,
  }));

  return (
    <div style={styles.panel}>
      <div style={styles.header}>
        <button style={styles.collapseBtn} onClick={toggleCollapsed}>
          <span style={styles.chevron}>{collapsed ? '▸' : '▾'}</span>
          <h2 style={styles.title}>🏧 Withdrawals & Sustainability</h2>
        </button>
      </div>

      {!collapsed && (
        <>
          {loading && <p style={styles.empty}>Loading...</p>}
          {error && !loading && <p style={styles.errorText}>{error}</p>}

          {data && !loading && !error && (
            <>
              <TransferReviewQueue candidates={candidates} onReview={handleReview} reviewingId={reviewingId} />

              {!data.hasSnapshotHistory && (
                <p style={styles.notice}>
                  Rates below are based on your net-worth snapshot history, which FinDash starts recording the first time you
                  visit this page - the longer you use it, the more accurate this gets. Withdrawal/contribution dollar amounts
                  are accurate from your full transaction history right away.
                </p>
              )}

              <div style={styles.headlineRow}>
                <div>
                  <div style={styles.headlineLabel}>Trailing 12 months, annualized</div>
                  <div style={{ ...styles.headlineValue, color: t12RateOverTarget ? '#ef4444' : '#00d4aa' }}>
                    {pctFmt(t12?.withdrawalRatePct)} <span style={styles.headlineSub}>withdrawal rate</span>
                  </div>
                  <div style={styles.headlineTarget}>Target: {targetRate.toFixed(1)}% (the "4% rule" default)</div>
                </div>
                {t12BeatingReturn !== null && (
                  <div style={{
                    ...styles.warnBox,
                    background: t12BeatingReturn ? 'rgba(239,68,68,0.08)' : 'rgba(0,212,170,0.08)',
                    border: `1px solid ${t12BeatingReturn ? 'rgba(239,68,68,0.3)' : 'rgba(0,212,170,0.3)'}`,
                  }}>
                    {t12BeatingReturn
                      ? `You're drawing ${pctFmt(t12.withdrawalRatePct)} but the portfolio only returned ${pctFmt(t12.portfolioReturnPct)} over the same 12 months - worth keeping an eye on if that continues.`
                      : `The portfolio returned ${pctFmt(t12.portfolioReturnPct)} over the trailing 12 months, more than the ${pctFmt(t12.withdrawalRatePct)} you drew - it's replenishing faster than you're drawing it down.`}
                  </div>
                )}
              </div>

              <div style={styles.periodGrid}>
                <PeriodCard label="This Month (annualized)" period={data.month} targetRate={targetRate} />
                <PeriodCard label="This Year (YTD, annualized)" period={data.year} targetRate={targetRate} />
                <PeriodCard label="Trailing 12 Months" period={data.trailing12} targetRate={targetRate} />
              </div>

              {chartData.some((c) => c.withdrawalRate != null) && (
                <div style={styles.chartSection}>
                  <div style={styles.chartLabel}>Monthly withdrawal rate vs. portfolio return</div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={chartData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
                      <XAxis dataKey="month" tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fill: '#6b7280', fontSize: 10, fontFamily: "'DM Mono', monospace" }} axisLine={false} tickLine={false} tickFormatter={(v) => `${v}%`} />
                      <Tooltip
                        contentStyle={{ background: '#1a2332', border: '1px solid #1e2a3a', borderRadius: '8px' }}
                        formatter={(v, name) => [v == null ? '—' : `${v.toFixed(1)}%`, name === 'withdrawalRate' ? 'Withdrawal rate' : 'Portfolio return']}
                      />
                      <ReferenceLine y={0} stroke="#374151" />
                      <Bar dataKey="portfolioReturn" name="Portfolio return" radius={[3, 3, 0, 0]} maxBarSize={14}>
                        {chartData.map((d, i) => (
                          <Cell key={i} fill={d.portfolioReturn == null ? 'transparent' : (d.portfolioReturn >= 0 ? '#00d4aa' : '#ef4444')} fillOpacity={0.5} />
                        ))}
                      </Bar>
                      <Bar dataKey="withdrawalRate" name="Withdrawal rate" fill="#f97316" radius={[3, 3, 0, 0]} maxBarSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                  <div style={styles.legendRow}>
                    <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#f97316' }} /> Withdrawal rate (monthly)</span>
                    <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#00d4aa' }} /> Portfolio return (up)</span>
                    <span style={styles.legendItem}><span style={{ ...styles.dot, background: '#ef4444' }} /> Portfolio return (down)</span>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  panel: {
    background: '#111827',
    border: '1px solid #1e2a3a',
    borderRadius: '16px',
    padding: '1.5rem',
    marginBottom: '1.25rem',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem' },
  title: { margin: 0, fontSize: '0.95rem', fontWeight: '700', color: '#fff', fontFamily: "'Syne', sans-serif" },
  collapseBtn: { display: 'flex', alignItems: 'center', gap: '0.5rem', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 },
  chevron: { color: '#6b7280', fontSize: '0.8rem', width: '0.8rem' },
  empty: { color: '#6b7280', fontSize: '0.85rem', margin: 0 },
  errorText: { color: '#ef4444', fontSize: '0.85rem', margin: 0 },
  notice: { color: '#6b7280', fontSize: '0.74rem', lineHeight: 1.5, marginBottom: '1rem' },
  headlineRow: { marginBottom: '1.1rem' },
  headlineLabel: { color: '#6b7280', fontSize: '0.72rem', fontFamily: "'Syne', sans-serif", marginBottom: '0.2rem' },
  headlineValue: { fontSize: '1.5rem', fontWeight: '800', fontFamily: "'DM Mono', monospace" },
  headlineSub: { fontSize: '0.85rem', fontWeight: '500', color: '#9ca3af', fontFamily: "'Syne', sans-serif" },
  headlineTarget: { color: '#6b7280', fontSize: '0.75rem', marginTop: '0.2rem' },
  warnBox: { borderRadius: '10px', padding: '0.7rem 0.9rem', fontSize: '0.78rem', color: '#d1d5db', lineHeight: 1.5, marginTop: '0.8rem' },
  periodGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: '0.75rem', marginBottom: '1.1rem' },
  periodCard: { background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '0.85rem' },
  periodLabel: { color: '#6b7280', fontSize: '0.68rem', fontFamily: "'Syne', sans-serif", marginBottom: '0.4rem' },
  periodMainRow: { marginBottom: '0.5rem' },
  periodDollar: { color: '#fff', fontSize: '0.95rem', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  periodStatsRow: { display: 'flex', gap: '1rem' },
  periodStat: { display: 'flex', flexDirection: 'column', gap: '0.15rem' },
  periodStatLabel: { color: '#6b7280', fontSize: '0.68rem' },
  periodStatValue: { fontSize: '0.85rem', fontWeight: '700', fontFamily: "'DM Mono', monospace" },
  chartSection: { marginTop: '0.5rem' },
  chartLabel: { color: '#9ca3af', fontSize: '0.72rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: '0.5rem', fontFamily: "'Syne', sans-serif" },
  legendRow: { display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '0.5rem' },
  legendItem: { display: 'flex', alignItems: 'center', gap: '0.4rem', color: '#9ca3af', fontSize: '0.72rem' },
  dot: { width: '8px', height: '8px', borderRadius: '50%' },
  reviewBox: {
    background: 'rgba(251,191,36,0.05)',
    border: '1px solid rgba(251,191,36,0.25)',
    borderRadius: '12px',
    padding: '1rem',
    marginBottom: '1.1rem',
  },
  reviewHeader: { color: '#fbbf24', fontSize: '0.85rem', fontWeight: '700', fontFamily: "'Syne', sans-serif", marginBottom: '0.3rem' },
  reviewSubtext: { color: '#9ca3af', fontSize: '0.74rem', lineHeight: 1.5, margin: '0 0 0.9rem 0' },
  reviewRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.75rem',
    flexWrap: 'wrap',
    padding: '0.6rem 0',
    borderTop: '1px solid rgba(251,191,36,0.12)',
  },
  reviewRowInfo: { flex: '1 1 160px', minWidth: 0 },
  reviewRowDesc: { color: '#e5e7eb', fontSize: '0.8rem', fontWeight: '600' },
  reviewRowMeta: { color: '#6b7280', fontSize: '0.7rem', marginTop: '0.15rem' },
  reviewBadge: {
    fontSize: '0.72rem',
    fontWeight: '700',
    fontFamily: "'DM Mono', monospace",
    border: '1px solid',
    borderRadius: '999px',
    padding: '3px 10px',
    whiteSpace: 'nowrap',
  },
  reviewActions: { display: 'flex', gap: '0.4rem' },
  confirmBtn: {
    background: 'rgba(0,212,170,0.12)',
    border: '1px solid rgba(0,212,170,0.4)',
    color: '#00d4aa',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
  ignoreBtn: {
    background: 'transparent',
    border: '1px solid #374151',
    color: '#9ca3af',
    borderRadius: '6px',
    padding: '5px 10px',
    fontSize: '0.72rem',
    fontWeight: '700',
    cursor: 'pointer',
  },
};
